import React, { useState, useRef, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiContentCopy } from "@mdi/js";
import DOMPurify from "dompurify";
import { copyToClipboard } from "../utils/emailAutomationHelpers";
import "./CopyEmailContentModal.css";

/**
 * Strips HTML tags to produce plain text for clipboard.
 */
const htmlToPlainText = (html) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = DOMPurify.sanitize(html);
  return div.textContent || div.innerText || "";
};

const CopyEmailContentModal = ({ show, onHide, subject, body, onToast }) => {
  const [copiedField, setCopiedField] = useState(null);
  const copiedTimerRef = useRef(null);

  useEffect(() => {
    if (!show) {
      setCopiedField(null);
    }
  }, [show]);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
      }
    };
  }, []);

  const handleCopy = async (text, field) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedField(field);
      if (onToast) {
        onToast("Content Copied", "success");
      }
      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
      }
      copiedTimerRef.current = setTimeout(() => setCopiedField(null), 2000);
    } else {
      if (onToast) {
        onToast("Failed to copy content", "error");
      }
    }
  };

  const sanitizedBody = body ? DOMPurify.sanitize(body) : "";

  return (
    <Modal show={show} onHide={onHide} centered className="copy-email-modal">
      <Modal.Header closeButton>
        <Modal.Title className="copy-email-title">
          Copy Email Content
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="copy-email-body">
        <div className="copy-email-field">
          <label className="copy-email-label">Subject</label>
          <div className="copy-email-content-row">
            <div className="copy-email-text copy-email-subject">
              {subject || "No subject"}
            </div>
            <Button
              variant="link"
              className="copy-email-btn"
              onClick={() => handleCopy(subject || "", "subject")}
              title="Copy subject"
            >
              <Icon
                path={mdiContentCopy}
                size={0.7}
                color={copiedField === "subject" ? "#198754" : "#0D6EFD"}
              />
            </Button>
          </div>
        </div>

        <div className="copy-email-field">
          <label className="copy-email-label">Body</label>
          <div className="copy-email-content-row copy-email-body-row">
            <div
              className="copy-email-text copy-email-body-text"
              dangerouslySetInnerHTML={{ __html: sanitizedBody || "No content" }}
            />
            <Button
              variant="link"
              className="copy-email-btn"
              onClick={() => handleCopy(htmlToPlainText(body), "body")}
              title="Copy body"
            >
              <Icon
                path={mdiContentCopy}
                size={0.7}
                color={copiedField === "body" ? "#198754" : "#0D6EFD"}
              />
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CopyEmailContentModal;
